// Adapters DTO (backend) → modelo da UI (Aggregator/Ticket). Funções puras
// (exceto a leitura do cache do scid-resolver). Aqui o motor "some": gravamos
// `podeIniciar`/`razao`/`bloqueadoPor` do servidor → resolveEligibility usa o servidor.
import type { Aggregator, Ticket, Customer, TicketAction, AggMetadata } from '../../domain/types'
import { LOCATION } from '../../domain/catalog'
import type { AttendanceJourneyDto, JourneyTicketDto, JourneyListItemDto, JourneyStepDto, CustomerDto, FriendlyCodeDto, MetadataDto } from '../dto/journey.dto'
import { ticketStatus, blockToRazao } from './status.map'
import { scidInfo } from './scid-resolver'
import { fieldDefs } from './customField.adapter'
import { ANONYMOUS_CUSTOMER, isTerminalCustomerEmail } from '../../domain/anonymousCustomer'

export const hhmm = (iso?: string | null): string | null => {
  if (!iso) {return null}
  const d = new Date(iso)
  if (isNaN(d.getTime())) {return null}
  // Converte UTC para UTC-3 (São Paulo)
  d.setHours(d.getHours() - 3)
  return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`
}
// Mesmo formato HH:MM de `previsaoLocal` (components/PrevisionBadge.tsx) — o badge concatena
// `ticket.prevision ?? previsaoLocal(estMin)` direto na UI, os dois precisam bater no formato.
export const formatHHmm = (iso?: string | null): string | null => {
  if (!iso) {return null}
  const d = new Date(iso)
  if (isNaN(d.getTime())) {return null}
  // Converte UTC para UTC-3 (São Paulo)
  d.setHours(d.getHours() - 3)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export const sessionDateTime = (iso?: string | null): string | null => {
  if (!iso) {return null}
  const d = new Date(iso)
  if (isNaN(d.getTime())) {return null}
  // Converte UTC para UTC-3 (São Paulo)
  d.setHours(d.getHours() - 3)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const hour = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${day}/${month} · ${hour}h${min}`
}

const isScheduledForToday = (iso?: string | null): boolean => {
  if (!iso) {return false}
  const d = new Date(iso)
  if (isNaN(d.getTime())) {return false}
  // Converte UTC para UTC-3 (São Paulo)
  d.setHours(d.getHours() - 3)
  const today = new Date()
  return d.toDateString() === today.toDateString()
}
const hasAnonymousIdentity = (c?: CustomerDto): boolean =>
  !!c && ((c.id != null && c.id <= 0) || isTerminalCustomerEmail(c.email))

export const customer = (c?: CustomerDto, fallbackName = 'Cliente sem nome'): Customer => ({
  id: c?.id ?? 0,
  nome: !c || hasAnonymousIdentity(c)
    ? ANONYMOUS_CUSTOMER.nome
    : c.name?.trim() || fallbackName,
  cpf: c?.cpf ?? '—',
  telefone: c?.phone ?? '—',
  email: c?.email ?? '',
})
const uiSequence = (sequence?: number): number => Math.max(0, (sequence ?? 1) - 1)
export const priority = (value?: number | string): number => {
  if (typeof value === 'number') {return value}
  if (!value) {return 0}
  const normalized = value.toLowerCase()
  if (normalized.includes('80')) {return 2}
  if (normalized.includes('prior') || normalized.includes('special')) {return 1}
  return 0
}
const estimateMinutes = (dto: JourneyTicketDto): number => {
  if (Number.isFinite(dto.estimatedDurationMinutes)) {return dto.estimatedDurationMinutes ?? 0}
  const start = dto.session?.estimatedStart ? Date.parse(dto.session.estimatedStart) : NaN
  const end = dto.session?.estimatedEnd ? Date.parse(dto.session.estimatedEnd) : NaN
  if (Number.isFinite(start) && Number.isFinite(end) && end > start) {return Math.max(1, Math.round((end - start) / 60000))}
  return 0
}
const ticketId = (dto: JourneyTicketDto): number => dto.ticketId ?? dto.id ?? 0
export const friendlyCode = (value?: FriendlyCodeDto | null): string => {
  if (value == null || value === '') {return '—'}
  if (typeof value === 'string') {return value}
  if (typeof value === 'number') {return String(value)}

  const candidates = [value.code, value.Code, value.value, value.friendlyCode, value.FriendlyCode, value.id, value.Id]
  const candidate = candidates.find((item) => item != null && item !== '')
  return candidate == null ? '—' : String(candidate)
}
const serviceId = (dto: JourneyTicketDto): number => dto.serviceId ?? dto.service?.id ?? 0
const serviceConfigId = (dto: JourneyTicketDto): number => dto.serviceConfigId ?? dto.serviceConfig?.id ?? dto.session?.serviceConfigId ?? 0
const sessionId = (dto: JourneyTicketDto): number => dto.sessionId ?? dto.session?.id ?? 0
const serviceName = (dto: JourneyTicketDto): string => dto.serviceName ?? dto.service?.name ?? 'Serviço sem nome'
const sortOrder = (dto: JourneyTicketDto, index = 0): number => dto.sortOrder ?? index + 1
const byBackendPosition = (a: JourneyTicketDto, b: JourneyTicketDto): number =>
  (a.sequence ?? 1) - (b.sequence ?? 1)
  || (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
  || ticketId(a) - ticketId(b)
const resourceId = (dto: JourneyTicketDto): string | null => {
  const id = dto.resourceId ?? dto.ResourceId ?? dto.resource?.id ?? dto.resource?.Id ?? dto.Resource?.id ?? dto.Resource?.Id
  return id == null || id === '' ? null : String(id)
}
const resourceName = (dto: JourneyTicketDto): string | null =>
  dto.resourceName ?? dto.ResourceName ?? dto.resource?.name ?? dto.resource?.Name ?? dto.Resource?.name ?? dto.Resource?.Name ?? null
const allowedActions = (dto: JourneyTicketDto): TicketAction[] | undefined => {
  const actions = dto.allowedActions ?? dto.actions
  if (!actions?.length) {return undefined}
  return actions.filter((a): a is TicketAction =>
    ['call', 'recall', 'start', 'complete', 'confirm', 'check', 'reopen', 'transfer', 'reschedule', 'reinsert'].includes(a))
}
const listCustomer = (dto: JourneyListItemDto): Customer => {
  if (dto.customer) {return displayCustomer(dto.customer, dto.aggregatorId)}
  if (dto.client) {return displayCustomer(dto.client, dto.aggregatorId)}
  if (dto.customerName || dto.customerId != null || dto.customerEmail) {
    return displayCustomer({
      id: dto.customerId,
      name: dto.customerName,
      cpf: dto.customerCpf,
      phone: dto.customerPhone,
      email: dto.customerEmail,
    }, dto.aggregatorId)
  }
  const ticketClient = dto.tickets?.find((t) => t.client?.name || t.client?.id)?.client
  if (ticketClient) {return displayCustomer(ticketClient, dto.aggregatorId)}
  return displayCustomer(undefined, dto.aggregatorId)
}
// metadata = bag de custom fields. O modelo (`templateKey`) vem do campo first-class
// `templateName` do backend, não de uma chave de vertical dentro da metadata.
export const metadata = (m?: MetadataDto, templateName?: string | null, templateId?: number | null): AggMetadata => {
  const fields: Record<string, string> = {}
  for (const [k, v] of Object.entries(m ?? {})) {if (v != null && v !== '') {fields[k] = v}}
  return { templateKey: templateName ?? null, templateId: templateId ?? null, fields }
}
export function ticket(dto: JourneyTicketDto, index = 0): Ticket {
  const scid = serviceConfigId(dto)
  const info = scidInfo(scid)
  const blockedByTicketIds = dto.blockedByTicketIds ?? dto.blockedBy?.map((item) => item.ticketId ?? 0).filter((id) => id > 0) ?? []
  const sessionIsToday = isScheduledForToday(dto.sessionStart ?? dto.session?.estimatedStart)
  const sessionExpired = dto.isOverdue ?? dto.scheduleState === 'Expired'
  const isOverdue = sessionExpired
  return {
    ticketId: ticketId(dto),
    codigoTicket: friendlyCode(dto.friendlyCode),
    serviceId: serviceId(dto),
    serviceConfigId: scid,
    sessionId: sessionId(dto),
    nomeServico: serviceName(dto),
    serviceConfigName: info?.nome || dto.serviceConfig?.name || undefined,
    sala: info?.sala ?? dto.serviceConfig?.roomName ?? '',
    estMin: estimateMinutes(dto),
    sequence: uiSequence(dto.sequence ?? index + 1),
    sortOrder: sortOrder(dto, index),
    status: ticketStatus(dto.status),
    attendanceType: dto.attendanceType === 'ONLINE' ? 'ONLINE' : 'PRESENTIAL',
    accessKey: dto.accessKey ?? null,
    smartCode: dto.smartCode ?? null,
    resourceId: resourceId(dto),
    resourceNome: resourceName(dto),
    iniciadoEm: hhmm(dto.startedAt),
    concluidoEm: hhmm(dto.completedAt),
    _startedAt: dto.startedAt ? Date.parse(dto.startedAt) : undefined,
    // verdade do servidor — resolveEligibility usa estes (motor local não roda)
    podeIniciar: dto.podeIniciar ?? dto.canStart,
    motivoBloqueio: dto.motivoBloqueio && !sessionIsToday ? dto.motivoBloqueio : dto.blockMessage ?? (sessionExpired ? 'Sessão vencida. Reagende para continuar.' : null),
    bloqueadoPor: blockedByTicketIds,
    timesCalled: dto.timesCalled,
    lastTimeCalled: dto.lastTimeCalled ?? null,
    prevision: formatHHmm(dto.prevision),
    sessionStart: dto.sessionStart ?? dto.session?.estimatedStart ?? null,
    sessionEnd: dto.sessionEnd ?? dto.session?.estimatedEnd ?? null,
    sessionDateTime: sessionDateTime(dto.sessionStart ?? dto.session?.estimatedStart),
    isOverdue,
    scheduleState: dto.scheduleState ?? null,
    scheduleReason: dto.scheduleReason ?? null,
    razao: blockToRazao(dto.blockReason ?? null),
    allowedActions: allowedActions(dto),
  }
}

// ── Nome do cliente em jornada sem nome real ────────────────────────────────
// Nome lixo/legado: vazio, '.', só dígitos (ex.: '1' criado por totem) ou
// placeholders genéricos. Identidades técnicas de terminal são normalizadas
// como AUTOATENDIMENTO, mesmo quando o cadastro legado traz outro nome.
const ANON_NAME_PLACEHOLDERS = new Set(['.', 'cliente sem nome'])
export const isJunkCustomerName = (nome?: string | null): boolean => {
  const n = nome?.trim().toLowerCase() ?? ''
  return !n || ANON_NAME_PLACEHOLDERS.has(n) || /^\d+$/.test(n)
}

// Identificador curto no padrão do totem: primeiros 4 + … + últimos 8
// (mesmo formato do truncateAggId no DetalhesScreen).
export const shortAggregatorId = (aggId?: string | null): string | null => {
  const id = aggId?.trim()
  if (!id) {return null}
  return id.length >= 12 ? `${id.slice(0, 4)}…${id.slice(-8)}` : id
}

// Cadastros identificados sem nome usam o identificador da jornada como fallback.
const displayCustomer = (c: CustomerDto | undefined, aggregatorId: string | undefined): Customer => {
  const base = customer(c)
  return isJunkCustomerName(base.nome) ? { ...base, nome: shortAggregatorId(aggregatorId) ?? base.nome } : base
}

export function journey(dto: AttendanceJourneyDto): Aggregator {
  const firstTicket = dto.tickets[0]
  const firstLocation = firstTicket?.location
  const createdAt = dto.createdAt ?? firstTicket?.createdAt
  return {
    aggregatorId: dto.aggregatorId,
    // O backend NÃO manda um friendlyCode no nível da jornada (só por ticket,
    // em `JourneyTicketDto.friendlyCode`). O código da jornada exibido no
    // bilhete impresso (ver JourneyTicketPrint) é o código do PRIMEIRO ticket
    // da jornada — mesma convenção usada nos seeds mock. Bug histórico: sem
    // isso, `agg.friendlyCode` ficava sempre undefined e caía num hash
    // quebrado do aggregatorId (gerava "J-9902" em vez de "J-0002").
    friendlyCode: firstTicket ? friendlyCode(firstTicket.friendlyCode) : undefined,
    status: dto.status,
    criadoEm: hhmm(createdAt) ?? '—',
    cliente: displayCustomer(
      dto.customer?.name?.trim() || hasAnonymousIdentity(dto.customer) ? dto.customer
      : dto.client?.name?.trim() || hasAnonymousIdentity(dto.client) ? dto.client
      : firstTicket?.client?.name?.trim() ? firstTicket.client
      : (dto.customer ?? dto.client ?? firstTicket?.client),
      dto.aggregatorId,
    ),
    localizacao: { id: firstLocation?.id ?? dto.locationId ?? LOCATION.id, nome: firstLocation?.name ?? dto.locationName ?? LOCATION.nome },
    prioridade: priority(dto.priority ?? firstTicket?.priority),
    tipoAtendimento: dto.attendanceType ?? firstTicket?.attendanceType ?? 'PRESENTIAL',
    metadata: metadata(dto.metadata, dto.templateName, dto.templateId),
    customFields: fieldDefs(dto.customFields ?? []),
    tickets: [...dto.tickets].sort(byBackendPosition).map((t, i) => ticket(t, i)),
    _createdAtMs: createdAt ? Date.parse(createdAt) : undefined,
  }
}

// Item de lista (leve): sintetiza tickets a partir de steps[] (status+sequence) —
// suficiente para pips/contadores/status da ListaScreen.
export function listItem(dto: JourneyListItemDto): Aggregator {
  const steps: JourneyStepDto[] = dto.steps ?? dto.tickets?.map((t, i) => ({
    ticketId: ticketId(t),
    friendlyCode: friendlyCode(t.friendlyCode),
    sequence: t.sequence ?? i + 1,
    status: t.status,
  })) ?? []
  return {
    aggregatorId: dto.aggregatorId,
    friendlyCode: dto.journeyCode,
    status: dto.status,
    criadoEm: hhmm(dto.createdAt) ?? '—',
    cliente: listCustomer(dto),
    localizacao: LOCATION,
    prioridade: priority(dto.priority),
    tipoAtendimento: 'PRESENTIAL',
    metadata: metadata(dto.metadata, dto.templateName),
    tickets: dto.tickets ? [...dto.tickets].sort(byBackendPosition).map((t, i) => ticket(t, i)) : steps.map(s => ({
      ticketId: s.ticketId ?? 0,
      codigoTicket: friendlyCode(s.friendlyCode),
      serviceId: s.serviceId ?? 0,
      serviceConfigId: s.serviceConfigId,
      sessionId: s.sessionId,
      sessionStart: s.sessionStart,
      sessionEnd: s.sessionEnd,
      resourceId: s.resourceId != null ? String(s.resourceId) : null,
      resourceNome: s.resourceName,
      nomeServico: s.serviceName ?? '',
      sala: '',
      estMin: 0,
      sequence: uiSequence(s.sequence),
      status: ticketStatus(s.status),
      attendanceType: 'PRESENTIAL' as const,
      accessKey: null,
      prevision: formatHHmm(s.prevision),
      isOverdue: dto.isOverdue ?? false,
      scheduleState: null,
    })),
    _createdAtMs: dto.createdAt ? Date.parse(dto.createdAt) : undefined,
  }
}
